import type { DropdownMenuItem, FormType } from "../../../components/types";
import type { SyntheticEvent } from "react-draft-wysiwyg";
import type { FC } from "react";
import type { SelectChangeEvent } from "@mui/material";

import {
  Box,
  Typography,
  Theme,
  Autocomplete,
  Chip,
  TextField,
  Stack,
} from "@mui/material";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { fireEvent } from "@testing-library/react";

import { useTranslation } from "react-i18next";

import { makeStyles } from "@mui/styles";

interface CampaignSettingsCustomProps {
  forms: FormType[];
  menuItems: DropdownMenuItem[];
  addEmptyPairingForm: () => void;
  getValuesToHide: (forms: FormType[]) => string[];
  handleFormControlChange: (event: SelectChangeEvent, index: number) => void;
  onAddFormIncludeChip: (formIndex: number, values: string[]) => void;
  onAddExcludeChip: (formIndex: number, values: string[]) => void;
  onDeleteForm: (index: number) => void;
}

const CampaignSettingsCustom: FC<CampaignSettingsCustomProps> = ({
  forms,
  menuItems,
  addEmptyPairingForm,
  getValuesToHide,
  handleFormControlChange,
  onAddExcludeChip,
  onAddFormIncludeChip,
  onDeleteForm,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.wrapper}>
      <Box sx={{ marginBottom: 2 }}>
        <Typography className={classes.title}>
          {t("campaign.settings.whereWillCampaignBeShown")}
        </Typography>
      </Box>
      {forms.map((form, index) => {
        return (
          <Box key={`dropdown-key-${index}`} className={classes.formContainer}>
            <Box className={classes.urlContainer}>
              <Box style={{ width: 280, marginBottom: 40 }}>
                <CustomDropdown
                  label={t("campaign.settings.url.title")}
                  id={`${index}-form-dropdown`}
                  value={form.control}
                  items={menuItems.map(item => {
                    return {
                      value: item.value,
                      title:
                        item.value === "beginsWith"
                          ? t("campaign.settings.url.startsWith")
                          : item.value === "contains"
                          ? t("campaign.settings.url.includes")
                          : t("campaign.settings.url.equals") ?? "",
                    };
                  })}
                  hiddenValues={getValuesToHide(forms)}
                  onChange={event => handleFormControlChange(event, index)}
                />
              </Box>
              <Box>
                <DeleteOutlineIcon
                  onClick={() => onDeleteForm(index)}
                  className={classes.icon}
                />
              </Box>
            </Box>

            <Autocomplete
              id={`include-${index}`}
              style={{ marginBottom: 40 }}
              multiple
              options={[]}
              freeSolo
              onChange={(_: SyntheticEvent, value: any) => {
                if (Array.isArray(value)) {
                  onAddFormIncludeChip(index, value);
                }
              }}
              value={form.include || []}
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    variant='outlined'
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant='outlined'
                  label={t("campaign.settings.includes")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Stack direction='row' spacing={1}>
                        <CustomButton
                          size='micro'
                          style={{ width: 40 }}
                          title='+'
                          onClick={() => {
                            const element = document.getElementById(
                              `include-${index}`,
                            );
                            if (element) {
                              fireEvent.keyDown(element, { key: "Enter" });
                            }
                          }}
                        />
                        {params.InputProps.endAdornment}
                      </Stack>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              id={`excludes-${index}`}
              multiple
              options={[]}
              freeSolo
              onChange={(event: any, value: any, reason) => {
                onAddExcludeChip(index, value);
              }}
              value={form.excludes || []}
              renderTags={(value: readonly string[], getTagProps) =>
                value.map((option: string, index: number) => (
                  <Chip
                    variant='outlined'
                    label={option}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={params => (
                <TextField
                  {...params}
                  variant='outlined'
                  label={t("campaign.settings.exclude")}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Stack direction='row' spacing={1}>
                        <CustomButton
                          size='micro'
                          style={{ width: 40 }}
                          title='+'
                          onClick={() => {
                            const element = document.getElementById(
                              `excludes-${index}`,
                            );
                            if (element) {
                              fireEvent.keyDown(element, { key: "Enter" });
                            }
                          }}
                        />
                        {params.InputProps.endAdornment}
                      </Stack>
                    ),
                  }}
                />
              )}
            />
          </Box>
        );
      })}
      {forms.length < 3 && (
        <CustomButton
          size='small'
          style={{ width: 180 }}
          type='secondary'
          title={t("campaign.settings.addCondition")}
          onClick={addEmptyPairingForm}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    paddingBottom: "16px",
    minHeight: 379,
  },
  title: {
    color: theme.palette.natural.t,
    fontSize: 18,
    fontWeight: "bold",
  },
  formContainer: {
    flex: 1,
    padding: 24,
    backgroundColor: theme.palette.natural.t8,
    marginBottom: 16,
    borderRadius: 8,
    maxWidth: 835,
  },
  urlContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  icon: {
    color: theme.palette.main.alert.o,
    fontSize: 24,
    cursor: "pointer",
  },
}));

export default CampaignSettingsCustom;
